// Full-viewport phone UI for mobile visitors.
// The pixel-house top-down map doesn't translate to small touch screens, so on
// mobile the phone IS the site. Apps lead to other pages; mail opens the same
// MailSystem desktop uses. Activated from main.js when the viewport is small.

import { APPS } from './world.config.js';
import { MailSystem } from './systems/MailSystem.js';

const LAST_VISIT_KEY = 'ian_world_last_visit';

export class MobileShell {
  constructor() {
    this.root = null;
    this.mail = new MailSystem();
    this._mount();
    // Stamp last visit so MailSystem's "since" filter behaves the same as desktop.
    try {
      localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
    } catch (_) {}
  }

  _mount() {
    const root = document.createElement('div');
    root.className = 'iw-mobile-shell';
    root.style.cssText = `
      position: fixed;
      inset: 0;
      background: #1a1614;
      color: #14110e;
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: env(safe-area-inset-top, 12px) 16px env(safe-area-inset-bottom, 12px);
      box-sizing: border-box;
      z-index: 60;
      overflow-y: auto;
      -webkit-tap-highlight-color: transparent;
    `;

    // Header tag — small, the "ianstandbridge" wordmark above the device
    const header = document.createElement('div');
    header.style.cssText = `
      color: #f4ead5;
      font-size: 11px;
      letter-spacing: 0.18em;
      text-transform: lowercase;
      opacity: 0.55;
      padding: 8px 0 6px;
    `;
    header.textContent = 'ianstandbridge.com';

    // Desktop hint — sits ABOVE the phone where eyes naturally land first
    // (the previous bottom-footer position was too easy to miss).
    const desktopHint = document.createElement('div');
    desktopHint.style.cssText = `
      color: #f4ead5;
      font-size: 12px;
      letter-spacing: 0.04em;
      opacity: 0.85;
      padding: 4px 12px 14px;
      text-align: center;
      line-height: 1.5;
      max-width: 380px;
    `;
    desktopHint.innerHTML = `you're getting the mobile view. <span style="opacity: 0.7">the full pixel house lives on desktop — pop this open on a laptop when you can.</span>`;

    // Phone frame — same aesthetic as desktop's PhoneSystem, sized to fill viewport
    const frame = document.createElement('div');
    frame.style.cssText = `
      width: 100%;
      max-width: 380px;
      flex: 1;
      background: #14110e;
      border: 6px solid #2a2018;
      border-radius: 32px;
      padding: 14px 12px 18px;
      box-shadow:
        inset 0 0 0 2px #3a2c20,
        0 24px 60px rgba(0, 0, 0, 0.65);
      display: flex;
      flex-direction: column;
      gap: 10px;
      image-rendering: pixelated;
      position: relative;
    `;

    // Speaker grille
    const speaker = document.createElement('div');
    speaker.style.cssText = `
      width: 44px;
      height: 4px;
      background: #2a2018;
      border-radius: 2px;
      margin: 0 auto;
    `;

    const screen = document.createElement('div');
    screen.style.cssText = `
      flex: 1;
      background: #F3E5C2;
      border-radius: 6px;
      padding: 14px 14px 18px;
      display: flex;
      flex-direction: column;
      gap: 14px;
      color: #14110e;
      overflow: hidden;
    `;

    // Status bar (time + battery)
    const statusBar = document.createElement('div');
    statusBar.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 10px;
      letter-spacing: 0.06em;
      color: #6b4f3a;
      text-transform: uppercase;
    `;
    const timeEl = document.createElement('span');
    timeEl.textContent = formatTime();
    const status = document.createElement('span');
    status.textContent = '●●●●  ☼  88%';
    statusBar.appendChild(timeEl);
    statusBar.appendChild(status);

    // Welcome line — frames the "phone IS the site" idea so mobile visitors
    // know this is intentional, not a degraded version.
    const welcome = document.createElement('div');
    welcome.style.cssText = `
      font-size: 11px;
      letter-spacing: 0.04em;
      color: #754C60;
      text-align: center;
      line-height: 1.5;
      padding: 4px 4px 0;
    `;
    welcome.innerHTML = `hey — i'm ian.<br/><span style="opacity:0.7">tap an app to take a look around.</span>`;

    // App grid
    const grid = document.createElement('div');
    grid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 14px;
      padding: 4px 0;
    `;
    for (const app of APPS) {
      grid.appendChild(this._makeAppTile(app));
    }

    // Mail tile — full-width, bottom of screen, matches desktop dock pattern
    const mailTile = this._makeMailTile();

    // Spacer to push the home indicator to the bottom
    const spacer = document.createElement('div');
    spacer.style.cssText = 'flex: 1;';

    // Home indicator pill
    const homeIndicator = document.createElement('div');
    homeIndicator.style.cssText = `
      width: 70px;
      height: 4px;
      background: #754C60;
      border-radius: 2px;
      margin: 0 auto;
      opacity: 0.45;
    `;

    screen.appendChild(statusBar);
    screen.appendChild(welcome);
    screen.appendChild(grid);
    screen.appendChild(mailTile);
    screen.appendChild(spacer);
    screen.appendChild(homeIndicator);

    frame.appendChild(speaker);
    frame.appendChild(screen);

    root.appendChild(header);
    root.appendChild(desktopHint);
    root.appendChild(frame);
    document.body.appendChild(root);
    this.root = root;

    // Tick the clock every minute so it doesn't stay frozen during long visits
    setInterval(() => { timeEl.textContent = formatTime(); }, 60_000);
  }

  _makeAppTile(app) {
    const tile = document.createElement('button');
    tile.type = 'button';
    tile.style.cssText = `
      background: transparent;
      border: 0;
      padding: 0;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      font-family: inherit;
      color: #14110e;
      -webkit-tap-highlight-color: transparent;
    `;

    const iconWrap = document.createElement('div');
    iconWrap.style.cssText = `
      width: 72px;
      height: 72px;
      border-radius: 16px;
      background: ${app.accent ?? '#754C60'};
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      box-shadow:
        inset 0 -2px 0 rgba(0, 0, 0, 0.18),
        inset 0 2px 0 rgba(255, 255, 255, 0.15),
        0 2px 0 rgba(0, 0, 0, 0.25);
      transition: transform 100ms ease;
      image-rendering: pixelated;
    `;

    const img = document.createElement('img');
    img.src = textureToDataURL(app.textureKey);
    img.alt = app.label;
    const iconPx = Math.round(60 * (app.iconScale ?? 1));
    img.style.cssText = `
      width: ${iconPx}px;
      height: ${iconPx}px;
      image-rendering: pixelated;
    `;
    iconWrap.appendChild(img);

    const label = document.createElement('span');
    label.textContent = app.label;
    label.style.cssText = `
      font-size: 10px;
      letter-spacing: 0.04em;
      max-width: 84px;
      text-align: center;
      line-height: 1.25;
    `;

    tile.appendChild(iconWrap);
    tile.appendChild(label);

    tile.addEventListener('touchstart', () => { iconWrap.style.transform = 'scale(0.92)'; }, { passive: true });
    tile.addEventListener('touchend', () => { iconWrap.style.transform = 'scale(1)'; });
    tile.addEventListener('touchcancel', () => { iconWrap.style.transform = 'scale(1)'; });
    tile.addEventListener('click', () => this._handleAppTap(app));

    return tile;
  }

  _makeMailTile() {
    const tile = document.createElement('button');
    tile.type = 'button';
    tile.style.cssText = `
      background: rgba(117, 76, 96, 0.12);
      border: 1px dashed rgba(117, 76, 96, 0.5);
      border-radius: 10px;
      padding: 12px 14px;
      display: flex;
      align-items: center;
      gap: 12px;
      cursor: pointer;
      font-family: inherit;
      color: #14110e;
      -webkit-tap-highlight-color: transparent;
    `;

    const iconBox = document.createElement('div');
    iconBox.style.cssText = `
      width: 36px;
      height: 36px;
      border-radius: 8px;
      background: #754C60;
      color: #F3E5C2;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
    `;
    iconBox.textContent = '✉';

    const text = document.createElement('div');
    text.style.cssText = `
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
      text-align: left;
    `;
    const title = document.createElement('div');
    title.textContent = 'mail';
    title.style.cssText = `
      font-size: 12px;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #754C60;
    `;
    const sub = document.createElement('div');
    sub.textContent = "what's new + side-quest hints";
    sub.style.cssText = `
      font-size: 10px;
      color: #6b4f3a;
      opacity: 0.85;
    `;
    text.appendChild(title);
    text.appendChild(sub);

    tile.appendChild(iconBox);
    tile.appendChild(text);

    tile.addEventListener('click', () => this.mail.open());

    return tile;
  }

  _handleAppTap(app) {
    if (app.kind === 'recursive') {
      this._showRecursiveFlash();
      return;
    }
    if (app.url) {
      window.open(app.url, '_blank', 'noopener,noreferrer');
    }
  }

  _showRecursiveFlash() {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(20, 17, 14, 0.94);
      color: #f4ead5;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 16px;
      padding: 24px;
      text-align: center;
      z-index: 80;
      font-family: inherit;
    `;
    overlay.innerHTML = `
      <div style="font-size:16px;letter-spacing:0.12em;color:#f6b93b;">you're already here</div>
      <div style="font-size:12px;color:rgba(244,234,213,0.7);line-height:1.5;max-width:260px;">
        you're holding it. the phone is the app. the app is the site.
      </div>
      <div style="font-size:10px;color:rgba(244,234,213,0.4);letter-spacing:0.08em;">— ian</div>
    `;
    overlay.addEventListener('click', () => overlay.remove());
    document.body.appendChild(overlay);
    setTimeout(() => overlay.remove(), 2400);
  }
}

function formatTime() {
  const d = new Date();
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

function textureToDataURL(textureKey) {
  const game = window.__iwGame__;
  if (!game) return transparentPixel();
  const tex = game.textures.get(textureKey);
  if (!tex || tex.key === '__MISSING') return transparentPixel();
  try {
    const src = tex.getSourceImage();
    if (src instanceof HTMLCanvasElement) return src.toDataURL();
    const c = document.createElement('canvas');
    c.width = src.width;
    c.height = src.height;
    const ctx = c.getContext('2d');
    if (ctx && src.width && src.height) {
      ctx.drawImage(src, 0, 0);
      return c.toDataURL();
    }
  } catch (_) {}
  return transparentPixel();
}

function transparentPixel() {
  return 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
}
